/**
 * Order status labels, colours and filter options — one source for the Orders view.
 *
 * Two of these are not order statuses in the backend:
 *
 * - READY is the `packed` status. An order that's packed is sitting at the store
 *   awaiting collection, which is what "ready" means operationally. There is no
 *   separate `ready` value, so Packed and Ready are the same thing — shown as Ready.
 * - REFUNDED lives on `refundStatus`, not `status`. Only a *processed* refund
 *   counts: the customer actually got their money back. A requested or failed
 *   refund is not a refund. It's filtered server-side via `refunded=true`.
 */

import type { Order } from './context/HUDContext';

export type StatusKey =
  | 'placed'
  | 'picking'
  | 'packed'
  | 'dispatched'
  | 'tracking'
  | 'arrived'
  | 'delivered'
  | 'returned'
  | 'cancelled'
  | 'deliveryFailed'
  | 'storeCancelled'
  | 'refunded';

export interface StatusMeta {
  label: string;
  color: string;
}

export const STATUS_META: Record<StatusKey, StatusMeta> = {
  placed: { label: 'Placed', color: '#ff6a3d' },      // orange — needs attention
  picking: { label: 'Packing', color: '#f5a623' },    // amber — being packed
  packed: { label: 'Ready', color: '#34eec1' },       // mint — at store, awaiting collection
  dispatched: { label: 'Dispatched', color: '#2f6bff' }, // blue — courier has it
  tracking: { label: 'Tracking', color: '#5a8dff' },  // lighter blue — on the road
  arrived: { label: 'Arrived', color: '#00ced1' },    // cyan — at the door
  delivered: { label: 'Delivered', color: '#16b981' }, // green — done
  returned: { label: 'Returned', color: '#ff69b4' },  // pink — came back
  cancelled: { label: 'Cancelled', color: '#ff4d54' }, // red — the customer cancelled
  deliveryFailed: { label: 'Delivery failed', color: '#ff8a3d' }, // orange — the driver's delivery failed
  storeCancelled: { label: 'Store cancelled', color: '#c2185b' }, // dark red — Deeliver/the store cancelled
  refunded: { label: 'Refunded', color: '#a855f7' },  // purple — money returned
};

/**
 * Display-only keys for a `cancelled` order, by who ended it (backend `cancelledBy`):
 * CANCELLED is kept for the customer (or unknown — orders from before the field).
 * Not backend statuses, so they are not filter options.
 */
const CANCELLED_BY_KEY: Record<string, StatusKey> = { courier: 'deliveryFailed', deeliver: 'storeCancelled' };
const DISPLAY_ONLY: ReadonlySet<StatusKey> = new Set<StatusKey>(['deliveryFailed', 'storeCancelled']);

/** A processed refund outranks the order's own status — the money went back. */
export function statusKeyOf(
  order: Pick<Order, 'status'> & { refundStatus?: string; cancelledBy?: string | null },
): StatusKey {
  if (order.refundStatus === 'processed') return 'refunded';
  const status = (order.status || 'placed').toLowerCase();
  if (status === 'cancelled' && order.cancelledBy && CANCELLED_BY_KEY[order.cancelledBy]) {
    return CANCELLED_BY_KEY[order.cancelledBy];
  }
  return (status in STATUS_META && !DISPLAY_ONLY.has(status as StatusKey) ? status : 'placed') as StatusKey;
}

/** Options for the status filter, in lifecycle order. */
export const STATUS_FILTER_OPTIONS: Array<{ key: StatusKey; label: string; color: string }> =
  (Object.keys(STATUS_META) as StatusKey[]).filter((key) => !DISPLAY_ONLY.has(key)).map((key) => ({
    key,
    label: STATUS_META[key].label,
    color: STATUS_META[key].color,
  }));
